import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { api } from '../api';
import type { ForumCategory, ForumPost, ForumThread, ForumThreadSummary } from '../types';
import '../styles/forum.css';
import { useAuth } from '../context/AuthContext';

interface ReplyTarget {
  parentId: string | null;
  authorName?: string | null;
}

interface PostItemProps {
  post: ForumPost;
  depth: number;
  answeredPostId: string | null;
  permissions: ForumThread['permissions'];
  onReply: (post: ForumPost) => void;
  onEdit: (post: ForumPost) => void;
  onDelete: (post: ForumPost) => void;
  onToggleAnswer: (post: ForumPost) => void;
  answerLoading: boolean;
  editingPostId: string | null;
  editContent: string;
  onEditChange: (value: string) => void;
  onSubmitEdit: (event: FormEvent, post: ForumPost) => void;
  onCancelEdit: () => void;
  savingEdit: boolean;
  highlightedPostId: string | null;
  onMove: (post: ForumPost) => void;
}

const HIGHLIGHT_DURATION_MS = 2000;
const POSTS_PER_PAGE = 10;

function flattenPosts(posts: ForumPost[]): ForumPost[] {
  const result: ForumPost[] = [];
  const stack = [...posts].reverse();
  while (stack.length > 0) {
    const post = stack.pop();
    if (!post) {
      continue;
    }
    result.push(post);
    if (post.replies && post.replies.length > 0) {
      for (let i = post.replies.length - 1; i >= 0; i -= 1) {
        stack.push(post.replies[i]);
      }
    }
  }
  return result;
}

function postContainsId(post: ForumPost, postId: string): boolean {
  if (post.id === postId) {
    return true;
  }
  return post.replies?.some((reply) => postContainsId(reply, postId)) ?? false;
}

function findRootIndexForPost(posts: ForumPost[], postId: string | null): number | null {
  if (!postId) {
    return null;
  }
  for (let index = 0; index < posts.length; index += 1) {
    if (postContainsId(posts[index], postId)) {
      return index;
    }
  }
  return null;
}

function computePageForPost(posts: ForumPost[], postId: string): number | null {
  const rootIndex = findRootIndexForPost(posts, postId);
  if (rootIndex === null) {
    return null;
  }
  return Math.floor(rootIndex / POSTS_PER_PAGE) + 1;
}

function findLatestPost(posts: ForumPost[]): ForumPost | null {
  const flat = flattenPosts(posts);
  if (flat.length === 0) {
    return null;
  }
  return flat.reduce((latest, post) =>
    new Date(post.createdAt).getTime() > new Date(latest.createdAt).getTime() ? post : latest
  );
}

function findPostById(posts: ForumPost[], postId: string | null): ForumPost | null {
  if (!postId) {
    return null;
  }
  const queue = [...posts];
  while (queue.length > 0) {
    const post = queue.shift();
    if (!post) {
      continue;
    }
    if (post.id === postId) {
      return post;
    }
    if (post.replies && post.replies.length > 0) {
      queue.push(...post.replies);
    }
  }
  return null;
}

function ForumPostItem({
  post,
  depth,
  answeredPostId,
  permissions,
  onReply,
  onEdit,
  onDelete,
  onToggleAnswer,
  answerLoading,
  editingPostId,
  editContent,
  onEditChange,
  onSubmitEdit,
  onCancelEdit,
  savingEdit,
  highlightedPostId,
  onMove
}: PostItemProps) {
  const isAnswer = answeredPostId === post.id;
  const showActions = !post.isDeleted;
  const isEditing = editingPostId === post.id;
  const isHighlighted = highlightedPostId === post.id;
  const canMovePost =
    depth > 0 &&
    post.canModerate &&
    (!post.replies || post.replies.length === 0);
  const className = [
    'forum-post',
    isAnswer ? 'forum-post--answer' : '',
    isHighlighted ? 'forum-post--highlight' : ''
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div id={`post-${post.id}`} className={className} style={{ marginInlineStart: depth * 24 }}>
      <div className="forum-post__meta">
        <div>
          <strong>{post.createdByName}</strong>
          <span className="forum-post__timestamp"> · {new Date(post.createdAt).toLocaleString()}</span>
          {post.isDeleted ? <span className="forum-post__deleted"> · deleted</span> : null}
          {post.editedAt ? (
            <span className="forum-post__edited">
              · edited {new Date(post.editedAt).toLocaleString()}
              {post.editedByName ? ` by ${post.editedByName}` : ''}
            </span>
          ) : null}
        </div>
        <div className="forum-post__meta-actions">
          {canMovePost ? (
            <button
              type="button"
              className="button button-secondary button button-secondary-small"
              onClick={() => onMove(post)}
              title="Move this reply to a different thread"
            >
              Move
            </button>
          ) : null}
          {isAnswer ? <span className="forum-post__answer-badge">Answer</span> : null}
        </div>
      </div>
      {isEditing ? (
        <form className="forum-post__edit" onSubmit={(event) => onSubmitEdit(event, post)}>
          <textarea value={editContent} onChange={(event) => onEditChange(event.target.value)} rows={4} required />
          <div className="forum-post__edit-actions">
            <button className="button" type="submit" disabled={savingEdit}>
              {savingEdit ? 'Saving…' : 'Save changes'}
            </button>
            <button className="button button-secondary" type="button" onClick={onCancelEdit}>
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <>
          <div className="forum-post__content">
            {post.isDeleted ? <em>[post removed]</em> : <p>{post.content}</p>}
          </div>
          <div className="forum-post__actions">
            {showActions && permissions.canReply ? (
              <button type="button" className="button button-secondary" onClick={() => onReply(post)}>
                Reply
              </button>
            ) : null}
            {showActions && post.canEdit ? (
              <button type="button" className="button button-primary" onClick={() => onEdit(post)}>
                Edit
              </button>
            ) : null}
            {showActions && post.canDelete ? (
              <button type="button" className="button button-secondary-danger" onClick={() => onDelete(post)}>
                Delete
              </button>
            ) : null}
            {showActions && permissions.canMarkAnswer ? (
              <button
                type="button"
                className="button button-secondary"
                onClick={() => onToggleAnswer(post)}
                disabled={answerLoading}
              >
                {isAnswer ? 'Unmark answer' : 'Mark as answer'}
              </button>
            ) : null}
          </div>
        </>
      )}
      {post.replies && post.replies.length > 0 ? (
        <div className="forum-post__replies">
          {post.replies.map((reply) => (
            <ForumPostItem
              key={reply.id}
              post={reply}
              depth={depth + 1}
              answeredPostId={answeredPostId}
              permissions={permissions}
              onReply={onReply}
              onEdit={onEdit}
              onDelete={onDelete}
              onToggleAnswer={onToggleAnswer}
              answerLoading={answerLoading}
              editingPostId={editingPostId}
              editContent={editContent}
              onEditChange={onEditChange}
              onSubmitEdit={onSubmitEdit}
              onCancelEdit={onCancelEdit}
              savingEdit={savingEdit}
              highlightedPostId={highlightedPostId}
              onMove={onMove}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function ForumThreadPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [thread, setThread] = useState<ForumThread | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [replyTarget, setReplyTarget] = useState<ReplyTarget | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [replying, setReplying] = useState(false);
  const [editingPostId, setEditingPostId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [editOriginalContent, setEditOriginalContent] = useState('');
  const [moveDialogPost, setMoveDialogPost] = useState<ForumPost | null>(null);
  const [moveSearchTerm, setMoveSearchTerm] = useState('');
  const [moveSuggestions, setMoveSuggestions] = useState<ForumThreadSummary[]>([]);
  const [moveSelectedThread, setMoveSelectedThread] = useState<ForumThreadSummary | null>(null);
  const [moveLookupPending, setMoveLookupPending] = useState(false);
  const [movingPost, setMovingPost] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState<ForumCategory[]>([]);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [categoryError, setCategoryError] = useState<string | null>(null);
  const [categorySelectedId, setCategorySelectedId] = useState('');
  const [categorySaving, setCategorySaving] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [closingReason, setClosingReason] = useState('');
  const [closing, setClosing] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [markingAnswer, setMarkingAnswer] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [highlightedPostId, setHighlightedPostId] = useState<string | null>(null);
  const [postPage, setPostPage] = useState(1);
  const [pendingScroll, setPendingScroll] = useState<{ postId: string; key: number } | null>(null);
  const [pinning, setPinning] = useState(false);
  const [lockTogglePending, setLockTogglePending] = useState(false);
  const [lockReasonSaving, setLockReasonSaving] = useState(false);
  const [lockReasonInput, setLockReasonInput] = useState('');
  const [watchSubmitting, setWatchSubmitting] = useState(false);
  const highlightTimeoutRef = useRef<number | null>(null);
  const previousThreadIdRef = useRef<string | null>(null);

  const threadId = id ? id.trim() : '';

  const applyHighlight = useCallback((postId: string) => {
    if (highlightTimeoutRef.current) {
      window.clearTimeout(highlightTimeoutRef.current);
      highlightTimeoutRef.current = null;
    }
    setHighlightedPostId(postId);
    highlightTimeoutRef.current = window.setTimeout(() => {
      setHighlightedPostId(null);
      highlightTimeoutRef.current = null;
    }, HIGHLIGHT_DURATION_MS);
  }, []);

  const scheduleScroll = useCallback(
    (posts: ForumPost[], postId: string) => {
      const targetPage = computePageForPost(posts, postId);
      if (targetPage) {
        setPostPage(targetPage);
      }
      setPendingScroll({ postId, key: Date.now() });
    },
    []
  );

  const refreshThread = useCallback(async () => {
    if (!threadId) {
      return;
    }
    setLoading(true);
    setNotFound(false);
    setError(null);
    try {
      const response = await api.forumThread(threadId);
      setThread(response);
    } catch (err) {
      const errorObject = err as Error & { status?: number };
      if (errorObject.status === 404) {
        setThread(null);
        setNotFound(true);
        setError('Thread not found or has been removed.');
      } else {
        setError(errorObject.message);
      }
    } finally {
      setLoading(false);
    }
  }, [threadId]);

  useEffect(() => {
    void refreshThread();
  }, [refreshThread]);

  const handleToggleWatchThread = useCallback(async () => {
    if (!thread) {
      return;
    }
    setWatchSubmitting(true);
    try {
      if (thread.viewerIsWatcher) {
        await api.unwatchForumThread(thread.id);
        setThread((current) => {
          if (!current) {
            return current;
          }
          const roles = current.viewerNotificationRoles || {
            created: false,
            participated: false,
            watched: false
          };
          return {
            ...current,
            viewerIsWatcher: false,
            viewerNotificationRoles: { ...roles, watched: false }
          };
        });
      } else {
        await api.watchForumThread(thread.id);
        setThread((current) => {
          if (!current) {
            return current;
          }
          const roles = current.viewerNotificationRoles || {
            created: false,
            participated: false,
            watched: false
          };
          return {
            ...current,
            viewerIsWatcher: true,
            viewerNotificationRoles: { ...roles, watched: true }
          };
        });
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setWatchSubmitting(false);
    }
  }, [thread, setThread, setError]);

  useEffect(() => {
    if (!thread) {
      return;
    }
    if (previousThreadIdRef.current !== thread.id) {
      previousThreadIdRef.current = thread.id;
      setPostPage(1);
    } else {
      const rootCount = thread.posts.length;
      const totalPages = Math.max(1, Math.ceil(rootCount / POSTS_PER_PAGE));
      if (postPage > totalPages) {
        setPostPage(totalPages);
      }
    }
  }, [thread, postPage]);

  useEffect(() => {
    if (!pendingScroll) {
      return;
    }
    const timeout = window.setTimeout(() => {
      const element = document.getElementById(`post-${pendingScroll.postId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        applyHighlight(pendingScroll.postId);
        setPendingScroll(null);
      }
    }, 60);
    return () => window.clearTimeout(timeout);
  }, [pendingScroll, applyHighlight, postPage, thread]);

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        window.clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (thread?.isLocked) {
      setLockReasonInput(thread.lockReason ?? '');
    } else {
      setLockReasonInput('');
    }
  }, [thread?.isLocked, thread?.lockReason]);

  useEffect(() => {
    if (!moveDialogPost || !thread?.permissions.canModerate) {
      setMoveSuggestions([]);
      setMoveLookupPending(false);
      return;
    }
    const query = moveSearchTerm.trim();
    if (!query) {
      setMoveSuggestions([]);
      setMoveLookupPending(false);
      return;
    }
    if (moveSelectedThread && query === moveSelectedThread.title.trim()) {
      setMoveSuggestions([]);
      setMoveLookupPending(false);
      return;
    }

    let cancelled = false;
    const timeout = window.setTimeout(() => {
      setMoveLookupPending(true);
      (async () => {
        try {
          const response = await api.forumThreads({ search: query, pageSize: 5 });
          let items = response.items.filter((item) => item.id !== thread.id);
          if (items.length === 0 && /^[0-9a-f-]{8,}$/i.test(query)) {
            try {
              const single = await api.forumThread(query);
              if (single.id !== thread.id) {
                const { permissions: _permissions, posts: _posts, ...summary } = single;
                items = [summary];
              }
            } catch {
              // ignore direct lookup failures
            }
          }
          if (!cancelled) {
            setMoveSuggestions(items);
          }
        } catch {
          if (!cancelled) {
            setMoveSuggestions([]);
          }
        } finally {
          if (!cancelled) {
            setMoveLookupPending(false);
          }
        }
      })();
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
      setMoveLookupPending(false);
    };
  }, [moveDialogPost, moveSearchTerm, moveSelectedThread, thread?.id, thread?.permissions.canModerate]);

  const handleReply = async (event: FormEvent) => {
    event.preventDefault();
    if (!thread || !replyContent.trim()) {
      return;
    }
    setReplying(true);
    setError(null);
    try {
      const response = await api.createForumPost(thread.id, {
        content: replyContent.trim(),
        parentId: replyTarget?.parentId ?? null
      });
      const newestPost = findLatestPost(response.posts);
      if (newestPost) {
        scheduleScroll(response.posts, newestPost.id);
      }
      setThread(response);
      setReplyContent('');
      setReplyTarget(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setReplying(false);
    }
  };

  const beginReply = (post: ForumPost) => {
    if (!thread?.permissions.canReply) {
      return;
    }
    setReplyTarget({ parentId: post.id, authorName: post.createdByName });
  };

  const beginEdit = (post: ForumPost) => {
    setEditingPostId(post.id);
    setEditContent(post.content ?? '');
    setEditOriginalContent(post.content ?? '');
  };

  const cancelEdit = () => {
    setEditingPostId(null);
    setEditContent('');
    setEditOriginalContent('');
  };

  const openMoveDialog = (post: ForumPost) => {
    if (post.replies && post.replies.length > 0) {
      setError('Posts with replies cannot be moved to a different thread.');
      return;
    }
    setMoveDialogPost(post);
    setMoveSearchTerm('');
    setMoveSuggestions([]);
    setMoveSelectedThread(null);
    setMoveLookupPending(false);
  };

  const closeMoveDialog = () => {
    setMoveDialogPost(null);
    setMoveSearchTerm('');
    setMoveSuggestions([]);
    setMoveSelectedThread(null);
    setMoveLookupPending(false);
    setMovingPost(false);
  };

  const handleMoveSearchChange = (value: string) => {
    setMoveSelectedThread(null);
    setMoveSearchTerm(value);
  };

  const handleMoveSelect = (threadSummary: ForumThreadSummary) => {
    setMoveSelectedThread(threadSummary);
    setMoveSearchTerm(threadSummary.title);
    setMoveSuggestions([]);
    setMoveLookupPending(false);
  };

  const handleMoveClear = () => {
    setMoveSelectedThread(null);
    setMoveSearchTerm('');
    setMoveSuggestions([]);
    setMoveLookupPending(false);
  };

  const handleMoveSubmit = async () => {
    if (!moveDialogPost || !moveSelectedThread) {
      return;
    }
    setMovingPost(true);
    setError(null);
    try {
      await api.updateForumPost(moveDialogPost.id, { moveToThreadId: moveSelectedThread.id });
      await refreshThread();
      closeMoveDialog();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setMovingPost(false);
    }
  };

  const fetchCategories = useCallback(async () => {
    setCategoryLoading(true);
    setCategoryError(null);
    try {
      const data = await api.forumCategories();
      setCategoryOptions(data);
    } catch (err) {
      setCategoryError((err as Error).message);
    } finally {
      setCategoryLoading(false);
    }
  }, []);

  const flattenedCategories = useMemo(() => {
    const result: Array<{ id: string; name: string; depth: number }> = [];
    const walk = (items: ForumCategory[], depth = 0) => {
      items.forEach((item) => {
        result.push({ id: item.id, name: item.name, depth });
        if (item.children && item.children.length > 0) {
          walk(item.children, depth + 1);
        }
      });
    };
    walk(categoryOptions);
    return result;
  }, [categoryOptions]);

  const handleBackToForum = useCallback(() => {
    const locationState = (location.state as { from?: string } | null) ?? null;
    const stateFrom = locationState?.from;
    if (stateFrom && stateFrom.startsWith('/forum')) {
      navigate(stateFrom);
      return;
    }

    const historyFrom =
      typeof window !== 'undefined' && window.history.state?.usr?.from
        ? (window.history.state.usr.from as string)
        : null;
    if (historyFrom && historyFrom.startsWith('/forum')) {
      navigate(historyFrom);
      return;
    }

    let referrerPath: string | null = null;
    if (typeof document !== 'undefined' && document.referrer) {
      try {
        const refUrl = new URL(document.referrer);
        referrerPath = `${refUrl.pathname}${refUrl.search}${refUrl.hash}`;
      } catch {
        referrerPath = null;
      }
    }
    if (referrerPath && referrerPath.startsWith('/forum')) {
      navigate(referrerPath);
      return;
    }

    navigate('/forum');
  }, [location, navigate]);

  const categorySegments = useMemo(() => {
    const current = thread?.category;
    if (!current) {
      return [] as Array<{ id: string; name: string; slug: string }>;
    }
    const path = current.path || [];
    if (path.length > 0 && path[path.length - 1].id === current.id) {
      return path;
    }
    return [...path, { id: current.id, name: current.name, slug: current.slug }];
  }, [thread?.category]);

  const openCategoryModal = () => {
    if (!thread) {
      return;
    }
    setCategoryModalOpen(true);
    setCategorySelectedId(thread.categoryId ?? '');
    setCategoryError(null);
  };

  const closeCategoryModal = () => {
    setCategoryModalOpen(false);
    setCategoryError(null);
    setCategorySaving(false);
  };

  const handleCategorySave = async () => {
    if (!thread || !categorySelectedId || categorySelectedId === thread.categoryId) {
      closeCategoryModal();
      return;
    }
    setCategorySaving(true);
    setCategoryError(null);
    try {
      const updated = await api.updateForumThreadCategory(thread.id, categorySelectedId);
      setThread(updated);
      closeCategoryModal();
    } catch (err) {
      setCategoryError((err as Error).message);
      setCategorySaving(false);
    }
  };

  useEffect(() => {
    if (categoryModalOpen && categoryOptions.length === 0 && !categoryLoading) {
      void fetchCategories();
    }
  }, [categoryModalOpen, categoryOptions.length, categoryLoading, fetchCategories]);

  const handleEdit = async (event: FormEvent, post?: ForumPost) => {
    event.preventDefault();
    const targetId = post?.id ?? editingPostId;
    if (!targetId) {
      return;
    }

    const trimmedContent = editContent.trim();
    const trimmedOriginal = editOriginalContent.trim();

    if (!trimmedContent || trimmedContent === trimmedOriginal) {
      setError('No changes to save.');
      return;
    }

    setSavingEdit(true);
    setError(null);
    try {
      await api.updateForumPost(targetId, { content: trimmedContent });
      await refreshThread();
      cancelEdit();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (post: ForumPost) => {
    const confirmRemoval = window.confirm('Delete this post?');
    if (!confirmRemoval) {
      return;
    }
    try {
      await api.deleteForumPost(post.id);
      await refreshThread();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const toggleAnswer = async (post: ForumPost) => {
    if (!thread) {
      return;
    }
    setMarkingAnswer(true);
    try {
      const response = await api.markForumAnswer(
        thread.id,
        thread.answeredPostId === post.id ? null : post.id
      );
      if (response.answeredPostId) {
        scheduleScroll(response.posts, response.answeredPostId);
      } else {
        setHighlightedPostId(null);
      }
      setThread(response);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setMarkingAnswer(false);
    }
  };

  const rootPosts = thread?.posts ?? [];
  const totalPostPages = Math.max(1, Math.ceil(rootPosts.length / POSTS_PER_PAGE));
  const pageStart = (postPage - 1) * POSTS_PER_PAGE;
  const visibleRootPosts = rootPosts.slice(pageStart, pageStart + POSTS_PER_PAGE);

  const answeredPost = useMemo(
    () => (thread ? findPostById(thread.posts, thread.answeredPostId) : null),
    [thread]
  );

  const scrollToPost = useCallback(
    (postId: string | null) => {
      if (!postId) {
        return;
      }
      scheduleScroll(thread ? thread.posts : [], postId);
    },
    [scheduleScroll, thread]
  );

  const goToPostPage = (nextPage: number) => {
    const clamped = Math.min(Math.max(nextPage, 1), totalPostPages);
    setPostPage(clamped);
  };

  const handlePinChange = async (event: ChangeEvent<HTMLInputElement>) => {
    if (!thread) {
      return;
    }
    const shouldPin = event.target.checked;
    setPinning(true);
    setError(null);
    try {
      if (shouldPin) {
        await api.pinForumThread(thread.id);
      } else {
        await api.unpinForumThread(thread.id);
      }
      await refreshThread();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setPinning(false);
    }
  };

  const handleLockChange = async (event: ChangeEvent<HTMLInputElement>) => {
    if (!thread) {
      return;
    }
    const shouldLock = event.target.checked;
    setLockTogglePending(true);
    setError(null);
    try {
      if (shouldLock) {
        await api.lockForumThread(thread.id, lockReasonInput.trim() || undefined);
      } else {
        await api.unlockForumThread(thread.id);
      }
      await refreshThread();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLockTogglePending(false);
    }
  };

  const handleLockReasonSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!thread || !thread.isLocked) {
      return;
    }
    setLockReasonSaving(true);
    setError(null);
    try {
      await api.lockForumThread(thread.id, lockReasonInput.trim() || undefined);
      await refreshThread();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLockReasonSaving(false);
    }
  };

  const handleCloseThread = async (event: FormEvent) => {
    event.preventDefault();
    if (!thread) {
      return;
    }
    setClosing(true);
    setError(null);
    try {
      await api.closeForumThread(thread.id, closingReason.trim() || undefined);
      setClosingReason('');
      await refreshThread();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setClosing(false);
    }
  };

  const handleReopen = async () => {
    if (!thread) {
      return;
    }
    setReopening(true);
    setError(null);
    try {
      await api.reopenForumThread(thread.id);
      await refreshThread();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setReopening(false);
    }
  };

  if (!threadId) {
    return (
      <div className="forum-thread">
        <div className="alert alert-error">Thread ID is missing.</div>
      </div>
    );
  }

  if (loading && !thread) {
    return <div className="loading loading-full">Loading thread…</div>;
  }

  if (notFound) {
    return (
      <div className="forum-thread">
        <div className="empty-state">
          <h2>Thread not found</h2>
          <p>The thread you are looking for was removed or never existed.</p>
          <button className="button button-secondary" type="button" onClick={() => navigate('/forum')}>
            Back to forum
          </button>
        </div>
      </div>
    );
  }

  if (error && !thread) {
    return <div className="alert alert-error">{error}</div>;
  }

  if (!thread) {
    return null;
  }

  const canChangeCategory = thread.permissions.canModerate || (user && user.id === thread.createdBy);
  const isWatchingThread = Boolean(thread.viewerIsWatcher);
  const watchButtonLabel = isWatchingThread ? 'Unwatch thread' : 'Watch thread';
  const threadNotificationRoles = thread.viewerNotificationRoles;

  const categoryDisplayName =
    categorySegments.length > 0 ? categorySegments.map((item) => item.name).join(' / ') : 'Uncategorized';

  return (
    <div className="forum-thread">
      <header className="forum-thread__header">
        <div className="forum-thread__title-group">
          <button className="button button-secondary" type="button" onClick={handleBackToForum}>
            ← Back to forum
          </button>
          <div className="forum-thread__title-row">
            <h1 className="forum-thread__title">
              {thread.title}{' '}
              <span className="forum-thread__category-info">
                ({categoryDisplayName}
                {canChangeCategory ? (
                  <>
                    {' '}
                    <button type="button" className="button button-tertiary" onClick={openCategoryModal}>
                      edit
                    </button>
                  </>
                ) : null}
                )
              </span>
            </h1>
          </div>
        </div>
        <div className="forum-thread__header-tags">
          {thread.status === 'open' ? (
            <span className="forum-thread__badge forum-thread__badge--open"><span className="material-symbols-outlined">support</span>Open</span>
          ) : null}
          {thread.answeredPostId ? (
            <span className="forum-thread__badge forum-thread__badge--answer"><span className="material-symbols-outlined">mark_chat_read</span>Answered</span>
          ) : null}
          {thread.status === 'closed' ? (
            <span className="forum-thread__badge forum-thread__badge--locked"><span className="material-symbols-outlined">close</span>Closed</span>
          ) : null}
          {thread.isPinned ? (
            <span className="forum-thread__badge forum-thread__badge--pinned"><span className="material-symbols-outlined">keep</span>Pinned</span>
          ) : null}
          {thread.isLocked ? (
            <span className="forum-thread__badge forum-thread__badge--locked"><span className="material-symbols-outlined">lock</span>Locked</span>
          ) : null}
          {thread.madeByThales ? (
            <span className="forum-thread__badge forum-thread__badge--thales">Made by Thales</span>
          ) : null}
          <button
            type="button"
            className={`button button-tertiary forum-thread__watch-button${
              isWatchingThread ? ' forum-thread__watch-button--active' : ''
            }`}
            onClick={handleToggleWatchThread}
            disabled={watchSubmitting}
          >
            {watchSubmitting
              ? isWatchingThread
                ? 'Unwatching…'
                : 'Watching…'
              : watchButtonLabel}
          </button>
        </div>
      </header>

      <section className="forum-thread__meta">
        <div>
          <strong>{thread.createdByName}</strong> opened this thread on{' '}
          {new Date(thread.createdAt).toLocaleString()}
        </div>
        <div>Last activity {new Date(thread.lastActivityAt).toLocaleString()}</div>
        {thread.isPinned ? (
          <div className="forum-thread__pinned-note">
            Pinned {thread.pinnedByName ? `by ${thread.pinnedByName}` : 'by an administrator'}
            {thread.pinnedAt ? ` on ${new Date(thread.pinnedAt).toLocaleString()}` : ''}
          </div>
        ) : null}
        {thread.closureReason ? (
          <div className="forum-thread__closure">
            Closed{thread.closedByName ? ` by ${thread.closedByName}` : ''}
            {thread.closedAt ? ` on ${new Date(thread.closedAt).toLocaleString()}` : ''}
            {thread.closureReason ? ` — ${thread.closureReason}` : ''}
          </div>
        ) : null}
        {thread.tags.length > 0 ? (
          <div className="forum-thread__tags">
            {thread.tags.map((tag) => (
              <span key={tag} className="tag">
                {tag}
              </span>
            ))}
          </div>
        ) : null}
        {threadNotificationRoles ? (
          <div className="forum-thread__notification-note">
            Notifications — Created: {threadNotificationRoles.created ? 'On' : 'Off'} · Participated:{' '}
            {threadNotificationRoles.participated ? 'On' : 'Off'} · Watched:{' '}
            {threadNotificationRoles.watched ? 'On' : 'Off'}
          </div>
        ) : null}
      </section>

      {thread.isLocked ? (
        <div className="forum-thread__lock-banner">
          <span className="forum-thread__lock-icon material-icons">lock</span>
          <div>
            <strong>Thread locked</strong>
            <div>
              Locked {thread.lockedByName ? `by ${thread.lockedByName}` : 'by an administrator'}
              {thread.lockedAt ? ` on ${new Date(thread.lockedAt).toLocaleString()}` : ''}
              {thread.lockReason ? ` — ${thread.lockReason}` : ''}
            </div>
          </div>
        </div>
      ) : null}

      {error ? <div className="alert alert-error">{error}</div> : null}

      {thread.permissions.canClose || thread.permissions.canPin || thread.permissions.canLock ? (
        <section className="forum-thread__moderation">
          {thread.permissions.canClose ? (
            thread.status === 'closed' ? (
              <div className="forum-thread__moderation-group">
                <button className="button" type="button" onClick={handleReopen} disabled={reopening}>
                  {reopening ? 'Reopening…' : 'Reopen thread'}
                </button>
              </div>
            ) : (
              <form onSubmit={handleCloseThread} className="forum-thread__close-form">
                <label htmlFor="close-reason">Close reason (optional)</label>
                <textarea
                  id="close-reason"
                  value={closingReason}
                  onChange={(event) => setClosingReason(event.target.value)}
                  rows={3}
                />
                <button className="button" type="submit" disabled={closing}>
                  {closing ? 'Closing…' : 'Close thread'}
                </button>
              </form>
            )
          ) : null}

          {thread.permissions.canPin ? (
            <div className="forum-thread__moderation-group">
              <label className="toggle-field">
                <input
                  type="checkbox"
                  checked={thread.isPinned}
                  onChange={handlePinChange}
                  disabled={pinning}
                />
                <span>
                  {thread.isPinned ? 'Pinned to category top' : 'Pin thread to category top'}
                </span>
              </label>
              {thread.isPinned && (thread.pinnedByName || thread.pinnedAt) ? (
                <span className="forum-thread__moderation-note">
                  Pinned {thread.pinnedByName ? `by ${thread.pinnedByName}` : 'by an administrator'}
                  {thread.pinnedAt ? ` on ${new Date(thread.pinnedAt).toLocaleString()}` : ''}
                </span>
              ) : (
                <span className="forum-thread__moderation-note">
                  Pinned threads stay at the top of their category.
                </span>
              )}
            </div>
          ) : null}

          {thread.permissions.canLock ? (
            <div className="forum-thread__moderation-group">
              <label className="toggle-field">
                <input
                  type="checkbox"
                  checked={thread.isLocked}
                  onChange={handleLockChange}
                  disabled={lockTogglePending}
                />
                <span>{thread.isLocked ? 'Thread locked (read-only)' : 'Lock thread (read-only)'}</span>
              </label>
              {thread.isLocked && (thread.lockedByName || thread.lockedAt) ? (
                <span className="forum-thread__moderation-note">
                  Locked {thread.lockedByName ? `by ${thread.lockedByName}` : 'by an administrator'}
                  {thread.lockedAt ? ` on ${new Date(thread.lockedAt).toLocaleString()}` : ''}
                </span>
              ) : (
                <span className="forum-thread__moderation-note">
                  Readers will see an optional message explaining why the thread is locked.
                </span>
              )}
              <form onSubmit={handleLockReasonSubmit} className="forum-thread__lock-form">
                <label htmlFor="lock-reason">Lock reason (optional)</label>
                <textarea
                  id="lock-reason"
                  value={lockReasonInput}
                  onChange={(event) => setLockReasonInput(event.target.value)}
                  rows={3}
                  disabled={lockTogglePending}
                />
                {thread.isLocked ? (
                  <button className="button button-secondary" type="submit" disabled={lockReasonSaving}>
                    {lockReasonSaving ? 'Updating…' : 'Update lock message'}
                  </button>
                ) : (
                  <span className="forum-thread__moderation-hint">
                    This message will be applied the next time you lock the thread.
                  </span>
                )}
              </form>
            </div>
          ) : null}
        </section>
      ) : null}

      {answeredPost ? (
        <section className="forum-thread__answer-highlight">
          <div className="forum-thread__answer-header">
            <h2>Accepted answer</h2>
            <span>
              by {answeredPost.createdByName} on {new Date(answeredPost.createdAt).toLocaleString()}
            </span>
          </div>
          <div className="forum-thread__answer-body">
            {answeredPost.isDeleted || !answeredPost.content ? (
              <em>[post removed]</em>
            ) : (
              <p>
                {answeredPost.content.length > 280
                  ? `${answeredPost.content.slice(0, 277)}…`
                  : answeredPost.content}
              </p>
            )}
          </div>
          <button
            className="button button-secondary"
            type="button"
            onClick={() => scrollToPost(answeredPost.id)}
          >
            Jump to answer
          </button>
        </section>
      ) : null}

      <section className="forum-thread__posts">
        {visibleRootPosts.length === 0 ? (
          <div className="empty-state">
            <h3>No replies yet</h3>
            <p>Be the first to respond to this thread.</p>
          </div>
        ) : null}
        {visibleRootPosts.map((post) => (
          <ForumPostItem
            key={post.id}
            post={post}
            depth={0}
            answeredPostId={thread.answeredPostId}
            permissions={thread.permissions}
            onReply={beginReply}
            onEdit={beginEdit}
            onDelete={handleDelete}
            onToggleAnswer={toggleAnswer}
            answerLoading={markingAnswer}
            editingPostId={editingPostId}
            editContent={editContent}
            onEditChange={(value) => setEditContent(value)}
            onSubmitEdit={handleEdit}
            onCancelEdit={cancelEdit}
            savingEdit={savingEdit}
            highlightedPostId={highlightedPostId}
            onMove={openMoveDialog}
          />
        ))}
      </section>

      {totalPostPages > 1 ? (
        <div className="pagination forum-thread__pagination">
          <div className="pagination__summary">
            Showing {(postPage - 1) * POSTS_PER_PAGE + 1}–
            {Math.min(rootPosts.length, postPage * POSTS_PER_PAGE)} of {rootPosts.length} replies
          </div>
          <div className="pagination__buttons">
            <button
              className="button button-secondary"
              type="button"
              onClick={() => goToPostPage(1)}
              disabled={postPage === 1}
            >
              « First
            </button>
            <button
              className="button button-secondary"
              type="button"
              onClick={() => goToPostPage(postPage - 1)}
              disabled={postPage === 1}
            >
              ‹ Prev
            </button>
            <span className="pagination__page-indicator">
              Page {postPage} of {totalPostPages}
            </span>
            <button
              className="button button-secondary"
              type="button"
              onClick={() => goToPostPage(postPage + 1)}
              disabled={postPage === totalPostPages}
            >
              Next ›
            </button>
            <button
              className="button button-secondary"
              type="button"
              onClick={() => goToPostPage(totalPostPages)}
              disabled={postPage === totalPostPages}
            >
              Last »
            </button>
          </div>
        </div>
      ) : null}

      {thread.permissions.canReply ? (
        <section className="forum-thread__reply">
          <form onSubmit={handleReply}>
            <label htmlFor="reply-content">
              {replyTarget ? `Replying to ${replyTarget.authorName ?? 'thread'}` : 'Add a reply'}
            </label>
            <textarea
              id="reply-content"
              value={replyContent}
              onChange={(event) => setReplyContent(event.target.value)}
              rows={5}
              required
              disabled={thread.status === 'closed'}
            />
            <div className="forum-thread__reply-actions">
              <button className="button" type="submit" disabled={replying || thread.status === 'closed'}>
                {replying ? 'Posting…' : 'Post reply'}
              </button>
              {replyTarget ? (
                <button className="button button-secondary" type="button" onClick={() => setReplyTarget(null)}>
                  Cancel reply
                </button>
      ) : null}

      {moveDialogPost ? (
        <div className="forum-modal" role="dialog" aria-modal="true" aria-labelledby="forum-move-title">
          <div className="forum-modal__backdrop" onClick={closeMoveDialog} aria-hidden="true" />
          <div className="forum-modal__panel">
            <header className="forum-modal__header">
              <h2 id="forum-move-title">Move post to another thread</h2>
              <button type="button" className="button button-secondary" onClick={closeMoveDialog}>
                Cancel
              </button>
            </header>
            <p className="forum-modal__description">
              Select a destination thread for this post. The original author will be notified about the move.
            </p>
            <div className="forum-modal__field">
              <label htmlFor="forum-move-search">Find thread</label>
              <input
                id="forum-move-search"
                type="text"
                value={moveSearchTerm}
                onChange={(event) => handleMoveSearchChange(event.target.value)}
                placeholder="Search by title or paste a thread ID"
                autoFocus
              />
            </div>
            {moveSelectedThread ? (
              <div className="forum-modal__selection">
                <div>
                  Moving to <strong>{moveSelectedThread.title}</strong>
                </div>
                <button type="button" className="button button-secondary" onClick={handleMoveClear}>
                  Clear selection
                </button>
              </div>
            ) : null}
            {!moveSelectedThread && moveLookupPending ? (
              <div className="forum-modal__status">Searching…</div>
            ) : null}
            {!moveSelectedThread && moveSuggestions.length > 0 ? (
              <ul className="forum-modal__results">
                {moveSuggestions.map((threadSummary) => (
                  <li key={threadSummary.id}>
                    <button type="button" onClick={() => handleMoveSelect(threadSummary)}>
                      <span className="forum-modal__result-title">{threadSummary.title}</span>
                      <span className="forum-modal__result-meta">
                        Updated {new Date(threadSummary.updatedAt).toLocaleDateString()} · Replies {threadSummary.replyCount}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            <footer className="forum-modal__actions">
              <button type="button" className="button" onClick={handleMoveSubmit} disabled={!moveSelectedThread || movingPost}>
                {movingPost ? 'Moving…' : 'Move post'}
              </button>
              <button type="button" className="button button-secondary" onClick={closeMoveDialog}>
                Cancel
              </button>
            </footer>
          </div>
        </div>
      ) : null}

      {categoryModalOpen ? (
        <div className="forum-modal" role="dialog" aria-modal="true" aria-labelledby="forum-category-title">
          <div className="forum-modal__backdrop" onClick={closeCategoryModal} aria-hidden="true" />
          <div className="forum-modal__panel">
            <header className="forum-modal__header">
              <h2 id="forum-category-title">Change thread category</h2>
              <button type="button" className="button button-secondary" onClick={closeCategoryModal}>
                Cancel
              </button>
            </header>
            <p className="forum-modal__description">
              Select the category where this thread should appear. Moving a thread reorders it in the forum listings.
            </p>
            {categoryError ? <div className="alert alert-error">{categoryError}</div> : null}
            {categoryLoading && categoryOptions.length === 0 ? (
              <div className="forum-modal__status">Loading categories…</div>
            ) : (
              <div className="forum-modal__field">
                <label htmlFor="forum-category-select">Category</label>
                <select
                  id="forum-category-select"
                  value={categorySelectedId}
                  onChange={(event) => setCategorySelectedId(event.target.value)}
                >
                  <option value="" disabled>
                    Select a category
                  </option>
                  {flattenedCategories.map((option) => (
                    <option key={option.id} value={option.id}>
                      {`${'— '.repeat(option.depth)}${option.name}`}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <footer className="forum-modal__actions">
              <button
                type="button"
                className="button"
                onClick={handleCategorySave}
                disabled={
                  categorySaving ||
                  !categorySelectedId ||
                  (thread && categorySelectedId === (thread.categoryId ?? ''))
                }
              >
                {categorySaving ? 'Saving…' : 'Save changes'}
              </button>
              <button type="button" className="button button-secondary" onClick={closeCategoryModal}>
                Cancel
              </button>
            </footer>
          </div>
        </div>
      ) : null}
            </div>
          </form>
        </section>
      ) : (
        <div
          className={`forum-thread__reply-disabled${
            thread.isLocked ? ' forum-thread__reply-disabled--locked' : ''
          }`}
        >
          {thread.isLocked
            ? `Replies are disabled because this thread is locked${
                thread.lockReason ? ` — ${thread.lockReason}` : '.'
              }`
            : 'Replies are disabled for this thread.'}
        </div>
      )}
    </div>
  );
}
